import axios from "axios";

const legacyHtmlToText = (value) => {
  const text = String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\n{3,}/g, "\n\n");
  return text.trim();
};

export const normalizeArticle = (article) => ({
  ...article,
  uid: String(article.uid),
  content: legacyHtmlToText(article.content),
  tags: Array.isArray(article.tags) ? article.tags : [],
  read_num: Number(article.read_num) || 0,
  author_name: String(article.author_name || "").trim(),
  updated_at: article.updated_at || "",
});

export const articleImageUrl = (article) => {
  const image = (article && article.img_url) || "";
  if (/^(https?:|data:|blob:)/.test(image)) return image;
  return `${process.env.VUE_APP_BACKEND_BASE_URL || "/api"}${image}`;
};

const youtubeVideoId = (url) => {
  const host = url.hostname.toLowerCase().replace(/^www\./, "");
  let videoId = "";

  if (host === "youtu.be") {
    videoId = url.pathname.split("/").filter(Boolean)[0] || "";
  } else if (
    host === "youtube.com" ||
    host === "m.youtube.com" ||
    host === "music.youtube.com" ||
    host === "youtube-nocookie.com"
  ) {
    const pathParts = url.pathname.split("/").filter(Boolean);
    if (url.pathname === "/watch") videoId = url.searchParams.get("v") || "";
    if (["shorts", "embed", "live"].includes(pathParts[0])) {
      videoId = pathParts[1] || "";
    }
  }

  return /^[A-Za-z0-9_-]{11}$/.test(videoId) ? videoId : "";
};

const isFacebookVideoUrl = (url) => {
  const host = url.hostname.toLowerCase().replace(/^www\./, "");
  if (host === "fb.watch") return url.pathname !== "/";
  if (host !== "facebook.com" && !host.endsWith(".facebook.com")) return false;

  return (
    /\/(videos|reel|watch|share\/v|share\/r)(\/|$)/i.test(url.pathname) ||
    url.searchParams.has("v") ||
    url.searchParams.has("story_fbid")
  );
};

export const safeVideoUrl = (value) => {
  try {
    const url = new URL(String(value || "").trim());
    if (!["http:", "https:"].includes(url.protocol)) return "";
    url.protocol = "https:";
    url.hash = "";
    return url.toString();
  } catch (err) {
    return "";
  }
};

export const articleVideoEmbed = (value) => {
  const safeUrl = safeVideoUrl(value);
  if (!safeUrl) return null;

  const url = new URL(safeUrl);
  const videoId = youtubeVideoId(url);
  if (videoId) {
    return {
      provider: "youtube",
      title: "YouTube 影片播放器",
      originalUrl: safeUrl,
      src: `https://www.youtube.com/embed/${videoId}?rel=0&playsinline=1`,
    };
  }

  if (isFacebookVideoUrl(url)) {
    return {
      provider: "facebook",
      title: "Facebook 影片播放器",
      originalUrl: safeUrl,
      src: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(
        safeUrl
      )}&show_text=false&width=1280`,
    };
  }

  return null;
};

const legacyArticles = async () => {
  const response = await axios.get(`${window.location.origin}/articles.json`);
  return response.data.map(normalizeArticle);
};

export const fetchArticles = async ({ admin = false } = {}) => {
  try {
    const response = await axios.get(admin ? "articles/admin" : "articles");
    const result = response.data.map(normalizeArticle);
    return result;
  } catch (err) {
    if (admin) throw err;
    return legacyArticles();
  }
};

export const fetchArticle = async (uid, { admin = false } = {}) => {
  try {
    const response = await axios.get(
      admin ? `articles/admin/${uid}` : `articles/${uid}`
    );
    if (
      !response.data ||
      typeof response.data !== "object" ||
      Array.isArray(response.data) ||
      response.data.uid == null
    ) {
      throw new Error("Invalid article response");
    }
    return normalizeArticle(response.data);
  } catch (err) {
    if (admin) throw err;
    if (
      err.response &&
      err.response.status < 500 &&
      process.env.NODE_ENV !== "development"
    ) {
      return null;
    }
    const articles = await legacyArticles();
    return articles.find((article) => article.uid === String(uid)) || null;
  }
};
